import Dialog from 'Components/_NewElements/Dialog';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import HistoryTabs from 'Components/HistoryTabs';
import Toolbar from 'Components/Toolbar';
import { withCapabilities } from 'Containers/CapabilityHandler';
import NavigationBlocker from 'Containers/FormEditingHandler/NavigationBlocker';
import { nowPartialDate } from 'neuro-utils';
import { useState, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useAppSelector, assertCapabilities } from 'Store/index';
import { ActionableErrorContext, Loggable, makeLog } from 'Utility/logger';
import PlatformCapabilities from '../../config/capabilities';
import DocumentHistoryItem from './components/DocumentHistoryItem';
import ReasonDialog from './components/ReasonDialog';
import RecordPage from './RecordPage';
import {
  ClinicalArchiveAPIError,
  createDraftArchiveDocument,
  createNewArchiveRevision,
  deleteArchiveDocument,
  deleteArchiveDocumentDraft,
  getArchiveDocumentList,
  getTemplateList,
  refreshArchiveDocument,
  sendArchiveDocument,
  updateArchiveDocumentText,
} from './utils/fetchers';
import { getAvailableArchiveTypes } from './utils/helpers';

import type { ISelectedAuthors } from './components/AuthorSelector';
import type { IReasonDialog } from './components/ReasonDialog';
import type { IArchiveObject, IClinicalTextObject } from './utils/helpers';
import type { IThemedDialog } from 'Components/_NewElements/Dialog';
import type { ICapabilityContextProps } from 'Containers/CapabilityHandler';
import type { ICompactVaultUser, INeuroArchiveTemplate } from 'neuro-data-structures';

// Return timestamp as partialdate or null
const timeStampToStartDate = (timestamp: number | null): PartialDate | null => {
  if (timestamp === null) {
    return null;
  } else {
    const d = new Date(timestamp);
    return [d.getFullYear(), d.getMonth() + 1, d.getDate()];
  }
};

const DEFAULT_CAPABILITY_GROUPS: IGroupACLMapping = {};
const PatientRecords = ({ capabilityGroups = DEFAULT_CAPABILITY_GROUPS }: IPatientRecords) => {
  const sessionData = useAppSelector(s => {
    /** Current user in session. */
    const user: ICompactVaultUser = {
      active: true,
      // user data should always be defined here but it's typed as possibly undefined...
      firstNames: s.session.data?.userfirstnames ?? '',
      lastNames: s.session.data?.userlastnames ?? '',
      userId: s.session.data?.useruuid ?? '',
    };
    return {
      org: s.session.data?.orgid,
      platform: s.session.platforms?.selected,
      patientId: s.session.data?.patientid ?? null,
      user,
    };
  });

  const useEndDate = sessionData.platform === 'ninmt';

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const [creatingNew, setCreatingNew] = useState<boolean>(false);
  // All templates for currently selected platform and org
  const [templateList, setTemplateList] = useState<INeuroArchiveTemplate[] | null>(null);
  // All available archive document types for user/org/platform
  const [docTypeList, setDocTypeList] = useState<string[] | null>(null);
  // Selected clinical document template
  const [selectedDocType, setSelectedDocType] = useState<string | null>(null);
  // List of archive docs
  const [documentsList, setDocumentsList] = useState<IArchiveObject[]>([]);
  // Fetched/created clinical document which is shown to the user -- indicates that a draft is initiated
  const [clinicalDocument, setClinicalDocument] = useState<IArchiveObject | null>(null);
  // Document generation start date selection
  const [startDate, setStartDate] = useState<PartialDate | null>(null);
  // Document generation end date selection
  const [endDate, setEndDate] = useState<PartialDate | null>(null);
  // Author selection
  const [selectedAuthors, setSelectedAuthors] = useState<ISelectedAuthors>({
    main: sessionData.user.userId,
    secondary: null,
  });
  // Additional information added with text boxes
  const [textContentNew, setTextContentNew] = useState<IClinicalTextObject[]>([]);
  // Custom timeframe for specific text content block
  const [timeframeNew, setTimeframeNew] = useState<IClinicalTextObject | undefined>(undefined);

  // Loading indicator for when something async is going on
  const [loading, setLoading] = useState<{
    send?: boolean;
    changeDate?: boolean;
    createNew?: boolean;
    deleteDocumentDraft?: boolean;
    savingTextChanges?: boolean;
    templateList?: boolean;
  }>({});
  // Reason dialog settings
  const [reasonDialogSettings, setReasonDialogSettings] = useState<IReasonDialog | null>(null);
  // Informationdialog settings for different kinds of informations
  const [informationDialogSettings, setInformationDialogSettings] = useState<IThemedDialog | null>(null);
  // Confirmationdialog settings for different kinds of confirmations
  const [confirmationDialogSettings, setConfirmationDialogSettings] = useState<IThemedDialog | null>(null);

  const clearCurrentDocumentData = () => {
    setClinicalDocument(null);
    setStartDate(null);
    if (useEndDate) setEndDate(nowPartialDate());
    setTextContentNew([]);
    setSelectedAuthors({ main: sessionData.user.userId, secondary: null });
  };

  const clearRecordsSettings = () => {
    setCreatingNew(false);
    setLoading({});
    clearCurrentDocumentData();
    // Always refresh the documents when going back to the list view. This helps with keeping the textContentVersion field up to date
    refreshDocumentList();
  };

  const setCurrentDocumentData = (curDoc: IArchiveObject) => {
    setClinicalDocument(curDoc);
    setStartDate(timeStampToStartDate(curDoc.dataStartTimestamp));
    if (useEndDate) setEndDate(timeStampToStartDate(curDoc.dataEndTimestamp));
    setSelectedAuthors({ main: curDoc.mainAuthor.userId, secondary: null });
  };

  // Function to refresh documents after any changes have occured
  const refreshDocumentList = (callbackThis?: () => void) => {
    const { org, platform } = sessionData;
    if (org && platform) {
      getArchiveDocumentList(org, platform)
        .then(docs => {
          if (Array.isArray(docs)) {
            setDocumentsList(docs);
          } else {
            setDocumentsList([]);
          }
          callbackThis?.();
        })
        .catch(() => {
          return;
        });
    } else {
      makeLog(
        Loggable.BugActionable,
        new Error('expected org and/or platform to exist'),
        new ActionableErrorContext(null, 'PatientRecords/refreshDocumentList/getArchiveDocumentList'),
      );
    }
  };

  const refreshCurrentDocument = (
    newStartDate?: PartialDate,
    newEndDate?: PartialDate,
    upTextContentVersion?: boolean,
    callbackThis?: () => void,
  ) => {
    if (!clinicalDocument) return;
    const thisTemplate = templateList?.find(tl => tl.id === clinicalDocument.templateId);
    const { org, platform } = sessionData;
    if (thisTemplate && org && platform) {
      refreshArchiveDocument(
        org,
        platform,
        thisTemplate.archiveType,
        clinicalDocument,
        newStartDate ?? clinicalDocument.dataStartTimestamp,
        newEndDate ?? null,
        upTextContentVersion,
      )
        .then(res => {
          if (res) {
            setCurrentDocumentData(res);
            callbackThis?.();
          }
        })
        .catch(() => {});
    }
  };

  const currentDocumentExists = async (
    action: 'savingTextChanges' | 'deleteDocumentDraft',
  ): Promise<boolean | 'fail'> => {
    setLoading({ ...loading, [action]: true }); // Show loading indicator for the check

    if (!clinicalDocument) return false;
    const { org, platform } = sessionData;
    if (!org || !platform) return false;

    const thisTemplate = templateList?.find(tl => tl.id === clinicalDocument.templateId);
    if (!thisTemplate) return 'fail';

    try {
      const res = await refreshArchiveDocument(
        org,
        platform,
        thisTemplate.archiveType,
        clinicalDocument,
        clinicalDocument.dataStartTimestamp,
        clinicalDocument.dataEndTimestamp,
      );

      setLoading({ ...loading, [action]: false });

      if (res) {
        return true; // The document still exists
      } else {
        return false; // The document was not found (returns null), so it was deleted
      }
    } catch {
      setLoading({ ...loading, [action]: false });
      return 'fail'; // In case the fetch fails
    }
  };

  // Load list of possible templates for the org, module and doctype
  const loadTemplates = useCallback(
    (docType: string): Promise<INeuroArchiveTemplate[]> => {
      const { org, platform } = sessionData;
      if (!org || !platform) return Promise.resolve([]);
      return getTemplateList(org, platform, docType)
        .then(templates => {
          const fetchedTemplates = Array.isArray(templates) ? templates : [];
          return fetchedTemplates;
        })
        .catch(() => {
          return [];
        });
    },
    [sessionData.org, sessionData.platform],
  );

  /* Update available archive templates for platform when platform changes. */
  useEffect(() => {
    if (!sessionData.platform) return;
    clearRecordsSettings();
    const archiveTypes = getAvailableArchiveTypes(capabilityGroups, sessionData.platform);
    setDocTypeList(archiveTypes);
    const templateFetches = archiveTypes.map(dt => loadTemplates(dt));

    let templatesToState: INeuroArchiveTemplate[] = [];

    setLoading({ ...loading, templateList: true });
    Promise.all(templateFetches)
      .then(tps => {
        tps.forEach(templateList => {
          if (templateList.length > 0) {
            templatesToState = templatesToState.concat(templateList);
          }
        });
        setTemplateList(templatesToState);
        setLoading({ ...loading, templateList: false });
        setSelectedDocType(archiveTypes[0] ?? null);
      })
      .catch(() => {});
  }, [sessionData.platform]);

  /** Save changes made to a specific text content block timeframe */
  useEffect(() => {
    if (timeframeNew) {
      saveTimeframeChanges();
    }
  }, [timeframeNew]);

  // Fetch updated/refreshed document for already sent documents, ready to be sent again
  const updateDocument = (id: string): void => {
    const { org, platform } = sessionData;
    const thisDocument = documentsList.find(d => d.id === id);
    const thisTemplate = templateList && templateList.find(tl => tl.id === thisDocument?.templateId);
    const thisStartDate = thisDocument?.dataStartTimestamp;
    if (thisTemplate && thisDocument && thisStartDate && org && platform) {
      if (!thisDocument.isSent) {
        refreshArchiveDocument(
          org,
          platform,
          thisTemplate.archiveType,
          thisDocument,
          thisDocument.dataStartTimestamp,
          thisDocument.dataEndTimestamp,
        )
          .then(res => {
            if (res) setCurrentDocumentData(res);
            else
              refreshDocumentList(() => {
                openInformationDialog(fm('patientRecords.failInformation'));
              });
          })
          .catch(() => {});
        return;
      }
      createNewArchiveRevision(org, platform, thisTemplate.archiveType, thisDocument)
        .then(amended => {
          if (amended) {
            setCurrentDocumentData(amended);
            refreshDocumentList();
          }
        })
        .catch(() => {
          return;
        });
    }
  };

  const updateTimeframe = async (): Promise<boolean> => {
    const { org, platform } = sessionData;
    if (clinicalDocument && timeframeNew && org && platform) {
      return updateArchiveDocumentText(org, platform, clinicalDocument.archiveType, clinicalDocument, [timeframeNew]);
    } else {
      return false;
    }
  };

  const kantaDelay: number | undefined | null = useAppSelector((s: IState) => {
    const orgSettings = s.settings.orgSettings;
    return typeof orgSettings === 'object' && 'kantaDelay' in orgSettings ? orgSettings.kantaDelay : null;
  });

  const saveTextChanges = async (): Promise<boolean> => {
    const { org, platform } = sessionData;
    if (!org || !platform) return false;
    if (clinicalDocument && textContentNew.length > 0) {
      return updateArchiveDocumentText(org, platform, clinicalDocument.archiveType, clinicalDocument, textContentNew);
    } else {
      return false;
    }
  };

  const saveTimeframeChanges = () => {
    setLoading({ ...loading, changeDate: true });
    updateTimeframe()
      .then(res => {
        if (res) {
          refreshCurrentDocument(undefined, undefined, true, () => {
            setTimeframeNew(undefined);
            setTimeout(() => setLoading({ ...loading, changeDate: false }), 200);
          });
        } else {
          openInformationDialog(fm('patientRecords.failInformation'));
        }
      })
      .catch(() => {});
  };

  const sendDocument = async (reason?: string): Promise<void> => {
    const thisTemplate = templateList?.find(tl => tl.id === clinicalDocument?.templateId);
    const { org, platform } = sessionData;
    if (clinicalDocument && thisTemplate && startDate && org && platform) {
      const isAuthor = clinicalDocument.mainAuthor.userId === sessionData.user.userId;
      const isAmending = clinicalDocument.archiveRevision > 1;
      setLoading({ ...loading, send: true });
      try {
        const patched = structuredClone(clinicalDocument);
        const saved: boolean = await saveTextChanges();
        if (saved) {
          patched.textContentVersion++;
          setClinicalDocument(patched);
        }
        const sent: 'sent' | 'notSent' | false = await sendArchiveDocument(
          org,
          platform,
          thisTemplate.archiveType,
          patched,
          startDate,
          endDate,
          reason ?? null,
          kantaDelay ?? null,
        );
        if (sent !== 'sent' && sent !== 'notSent') {
          // "Sending" the document has failed completely.
          openInformationDialog(fm('patientRecords.failInformation'));
        } else if (sent === 'sent') {
          // Sending the document to our back end was successful
          // The document was also sent forward from our system
          const sendText =
            (isAmending
              ? isAuthor
                ? thisTemplate.onAmendSendInfoTextForAuthor
                : thisTemplate.onAmendSendInfoTextForOther
              : isAuthor
                ? thisTemplate.onSendInfoTextForAuthor
                : thisTemplate.onSendInfoTextForOther) ?? fm('patientRecords.sendInformation');
          openInformationDialog(sendText, false);
          clearRecordsSettings();
        } else {
          // Sending the document to our back end was successful
          // The document was not sent forward to whatever HCP system
          openInformationDialog(fm('patientRecords.saveInformation'));
          clearRecordsSettings();
        }
      } catch (err) {
        setLoading({ ...loading, send: false });
        if (err instanceof ClinicalArchiveAPIError) {
          const failureMessageDisplay = err.failureMessageDisplay();
          if (failureMessageDisplay) {
            openInformationDialog(failureMessageDisplay, false);
          } else {
            openInformationDialog(fm('patientRecords.failInformation'));
          }
        } else {
          openInformationDialog(fm('patientRecords.failInformation'));
        }
      }
    }
  };

  const deleteDocument = (documentId: string, reason: string) => {
    const thisDocument = documentsList.find(d => d.id === documentId);
    const thisTemplate = templateList?.find(tl => tl.id === thisDocument?.templateId);
    const { org, platform } = sessionData;
    if (thisDocument && thisTemplate && org && platform) {
      deleteArchiveDocument(org, platform, thisTemplate.archiveType, thisDocument, reason)
        .then(res => {
          clearCurrentDocumentData();
          refreshDocumentList();
          if (res) openInformationDialog(fm('patientRecords.deleteInformation'));
          else openInformationDialog(fm('patientRecords.failInformation'));
        })
        .catch(() => {
          return;
        });
    }
  };

  const deleteDocumentDraft = (document: IArchiveObject): void => {
    const thisTemplate = templateList?.find(tl => tl.id === document.templateId);
    const { org, platform } = sessionData;
    if (thisTemplate && !document.isSent && org && platform) {
      setLoading({ ...loading, deleteDocumentDraft: true });
      deleteArchiveDocumentDraft(org, platform, thisTemplate.archiveType, document)
        .then(async res => {
          clearRecordsSettings();
          setLoading({ ...loading, deleteDocumentDraft: false });
          setConfirmationDialogSettings(null);
          if (res) {
            openInformationDialog(fm('patientRecords.draftDeleteInformation'));
          } else {
            const documentExists = await currentDocumentExists('deleteDocumentDraft');
            if (documentExists !== false) {
              openInformationDialog(fm('patientRecords.failInformation'));
            } else {
              openInformationDialog(
                fm(`${fm('patientRecords.failInformation')}:\n${fm('patientRecords.draftAlreadyDeletedInformation')}`),
              );
            }
          }
        })
        .catch((err: unknown) => {
          makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'delete draft patient record'));
        });
    }
  };

  const verifyDocumentExistenceAndClose = async () => {
    const documentExists = await currentDocumentExists('savingTextChanges');

    if (typeof documentExists !== 'boolean') {
      openInformationDialog(fm('patientRecords.failInformation'));
      clearRecordsSettings();
      return;
    }
    if (documentExists) {
      clearRecordsSettings();
    } else {
      openInformationDialog(
        `${fm('patientRecords.failInformation')}:\n${fm('patientRecords.draftAlreadyDeletedInformation')}`,
      );
      clearRecordsSettings();
    }
  };

  const createArchive = (template: INeuroArchiveTemplate) => {
    const { org, platform } = sessionData;
    if (!org || !platform) return;
    if (!selectedAuthors.main || !startDate) return;
    setLoading({ ...loading, createNew: true });
    createDraftArchiveDocument(
      org,
      platform.toUpperCase(),
      template.archiveType,
      selectedAuthors.main,
      selectedAuthors.secondary,
      template.id,
      startDate,
      endDate,
    )
      .then(doc => {
        if (doc) {
          setLoading({ ...loading, createNew: false });
          setCurrentDocumentData(doc);
          setSelectedDocType(doc.archiveType); // Automatically select tab corresponding to created document
          refreshDocumentList();
        }
      })
      .catch(() => {
        return;
      });
  };

  const openReasonDialog = (id: string, type: 'delete' | 'newDraft') => {
    const thisDocument = documentsList.find(d => d.id === id);
    const thisTemplate = templateList?.find(tl => tl.id === thisDocument?.templateId);
    setReasonDialogSettings({
      type,
      open: true,
      cancelCallback: () => {
        setReasonDialogSettings(null);
      },
      confirmCallback: (reason: string) => {
        setReasonDialogSettings(null);
        if (type === 'delete') deleteDocument(id, reason);
        if (type === 'newDraft') sendDocument(reason).catch(() => {});
      },
      supportsExternalDeletion: thisTemplate ? thisTemplate.supportsDelete : false,
    });
  };

  const openInformationDialog = (text: string, hasTimeout = true) => {
    setInformationDialogSettings({
      open: true,
      children: text,
      onClose: () => setInformationDialogSettings(null),
      closeTimeout: hasTimeout ? 1500 : undefined,
    });
  };

  const openDraftDeleteConfirmation = (document: IArchiveObject) => {
    setConfirmationDialogSettings({
      open: true,
      children: fm('patientRecords.deleteDraftConfirmationText'),
      onClose: () => {
        setConfirmationDialogSettings(null);
      },
      dialogActions: [
        {
          text: 'general.cancel',
          onClick: () => {
            setConfirmationDialogSettings(null);
          },
        },
        {
          text: 'general.delete',
          onClick: () => {
            deleteDocumentDraft(document);
          },
          colorScheme: 'error',
          filled: true,
          alternate: true,
        },
      ],
    });
  };

  const openSavingConfirmation = (document: IArchiveObject, callbackThis?: (canceled: boolean) => void) => {
    const thisTemplate = templateList?.find(tl => tl.id === document.templateId);
    if (thisTemplate)
      setConfirmationDialogSettings({
        open: true,
        children: fm('patientRecords.saveConfirmationText'),
        dialogActions: [
          {
            text: 'general.no',
            onClick: () => {
              setConfirmationDialogSettings(null);
              callbackThis?.(true);
            },
          },
          {
            text: 'general.yes',
            filled: true,
            alternate: true,
            onClick: () => {
              setLoading({ ...loading, savingTextChanges: true });
              saveTextChanges()
                .then(res => {
                  if (res) {
                    refreshCurrentDocument(undefined, undefined, true, () => {
                      openInformationDialog(fm('patientRecords.saveInformation'), false);
                      setTextContentNew([]);
                      setLoading({ ...loading, savingTextChanges: false });
                      callbackThis?.(false);
                    });
                  } else {
                    openInformationDialog(fm('patientRecords.failInformation'));
                  }
                })
                .catch(() => {});
              setConfirmationDialogSettings(null);
            },
          },
        ],
      });
  };

  const onChangeStartDate = (newStartDate: PartialDate) => {
    if (clinicalDocument && !clinicalDocument.isSent) {
      setLoading({ ...loading, changeDate: true });
      // Refresh document when startDate changes
      refreshCurrentDocument(newStartDate, undefined, undefined, () => setLoading({ ...loading, changeDate: false }));
    } else {
      setStartDate(newStartDate);
    }
  };
  const onChangeEndDate = (newEndDate: PartialDate) => {
    if (clinicalDocument && !clinicalDocument.isSent) {
      setLoading({ ...loading, changeDate: true });
      // Refresh document when endDate changes
      refreshCurrentDocument(undefined, newEndDate, undefined, () => setLoading({ ...loading, changeDate: false }));
    } else {
      setEndDate(newEndDate);
    }
  };

  const onChangeSelectedNewDocumentTemplate = (template: INeuroArchiveTemplate): void => {
    const previousDocument = documentsList.find(d => d.templateId === template.id);
    // Set startDate with previous documents endDate if exists
    if (previousDocument?.dataEndTimestamp) setStartDate(timeStampToStartDate(previousDocument.dataEndTimestamp));

    if (useEndDate) setEndDate(nowPartialDate());
  };

  const patientRecordsCapability = assertCapabilities([PlatformCapabilities.CLINICAL_RECORDS], capabilityGroups);

  const users = useAppSelector(state => state.session.orgUsers ?? []);

  const documentsByType = documentsList.filter(d => d.archiveType === selectedDocType);

  const thisTemplate = templateList?.find(tl => tl.id === clinicalDocument?.templateId);

  return patientRecordsCapability ? (
    <NavigationBlocker blocked={!!(clinicalDocument && textContentNew.length > 0 ? clinicalDocument : null)}>
      <>
        <Toolbar
          current="patientRecords"
          editing={clinicalDocument || creatingNew ? (clinicalDocument?.archiveId ?? 'current') : undefined}
          cancelDraft={() => () => {
            if (textContentNew.length > 0) {
              if (clinicalDocument) {
                openSavingConfirmation(clinicalDocument, () => {
                  clearRecordsSettings();
                });
              }
            } else {
              if (!clinicalDocument) {
                clearRecordsSettings();
              } else {
                verifyDocumentExistenceAndClose().catch(() => {});
              }
            }
          }}
          hiddenElements={['saveButton', 'nextSection', 'dropDown']}
          customLocales={{ cancel: { title: 'general.close', toolTip: null } }}
          cancelButtonDisabled={!!(creatingNew && clinicalDocument)}
        />
        <DocumentWrapper>
          {!creatingNew && !clinicalDocument ? (
            <DocumentHeader
              headerId="patientRecords.title"
              editButtons={[
                <ActionButtonRounded
                  width={26}
                  key="create"
                  text="patientRecords.newDocument"
                  onClick={() => setCreatingNew(true)}
                  disabled={!templateList || templateList.filter(tl => tl.isActive).length === 0} // Disabled if no active templates
                  loading={loading.templateList}
                  disabledTooltip={<>{fm('patientRecords.noTemplates')}</>}
                />,
              ]}
            />
          ) : undefined}
          {creatingNew || clinicalDocument ? (
            <RecordPage
              document={clinicalDocument}
              docTypeList={docTypeList}
              templateList={templateList ?? []}
              session={{ users, user: sessionData.user }}
              unsentTemplateIds={documentsList.filter(doc => !doc.isSent).map(notsent => notsent.templateId)}
              createArchive={createArchive}
              deleteDraft={openDraftDeleteConfirmation}
              saveDraft={
                clinicalDocument
                  ? () => {
                      if (textContentNew.length > 0) {
                        setLoading({ ...loading, savingTextChanges: true });
                        saveTextChanges()
                          .then(res => {
                            if (res) {
                              refreshCurrentDocument(undefined, undefined, true, () => {
                                openInformationDialog(fm('patientRecords.saveInformation'), false);
                                clearRecordsSettings();
                                setLoading({ ...loading, savingTextChanges: false });
                              });
                            } else {
                              openInformationDialog(fm('patientRecords.failInformation'));
                              setLoading({ ...loading, savingTextChanges: false });
                            }
                          })
                          .catch(() => {
                            openInformationDialog(fm('patientRecords.failInformation'));
                            setLoading({ ...loading, savingTextChanges: false });
                          });
                      } else {
                        verifyDocumentExistenceAndClose().catch(() => {});
                      }
                    }
                  : () => ''
              }
              sendText={
                clinicalDocument
                  ? !clinicalDocument.archiveId
                    ? () => sendDocument()
                    : () => openReasonDialog(clinicalDocument.id, 'newDraft')
                  : undefined
              }
              sendButtonLocalizationKey={
                thisTemplate?.sendMaxRevision === 0 ||
                (thisTemplate?.sendMaxRevision &&
                  thisTemplate.sendMaxRevision > 0 &&
                  clinicalDocument?.archiveRevision &&
                  thisTemplate.sendMaxRevision < clinicalDocument.archiveRevision)
                  ? 'general.save'
                  : 'patientRecords.saveAndSend'
              }
              // Authors
              selectedAuthors={selectedAuthors}
              setSelectedAuthors={setSelectedAuthors}
              // StartDate
              startDate={startDate}
              setStartDate={onChangeStartDate}
              // EndDate
              endDate={endDate}
              setEndDate={onChangeEndDate}
              // Text content
              textContent={textContentNew}
              setTextContent={setTextContentNew}
              // Timeframe
              timeframe={timeframeNew}
              setTimeframe={setTimeframeNew}
              // Loading info
              loading={loading}
              onChangeSelectedNewDocumentTemplate={onChangeSelectedNewDocumentTemplate}
              // Whether to use endDate field
              useEndDate={useEndDate}
            />
          ) : (
            <HistoryTabs
              indexSelectionTools={{
                index: (docTypeList ?? []).findIndex(dt => dt === selectedDocType),
                changeFunction: (index: number) => {
                  if (docTypeList?.[index]) setSelectedDocType(docTypeList[index]);
                },
              }}
            >
              {(docTypeList ?? []).map(dt => ({
                title: <>{dt}</>,
                count: documentsList.filter(dl => dl.archiveType === dt).length,
                content: (
                  <>
                    {documentsByType.map((document, i) => {
                      const template = (templateList ?? []).find(tl => tl.id === document.templateId);
                      return (
                        template && (
                          <DocumentHistoryItem
                            key={document.id || 'current'}
                            index={i}
                            document={document}
                            template={template}
                            users={users}
                            updateDocument={() => updateDocument(document.id)}
                            deleteDocument={() => {
                              openReasonDialog(document.id, 'delete');
                            }}
                            unsentExists={documentsByType.some(d => !d.isSent)}
                            deleteDraft={() => openDraftDeleteConfirmation(document)}
                          />
                        )
                      );
                    })}
                  </>
                ),
              }))}
            </HistoryTabs>
          )}
        </DocumentWrapper>

        {informationDialogSettings && (
          /** Dialog with ok button or timeout */
          <Dialog
            open={informationDialogSettings.open}
            type="info"
            onClose={informationDialogSettings.onClose}
            closeTimeout={informationDialogSettings.closeTimeout}
          >
            {informationDialogSettings.children}
          </Dialog>
        )}

        {reasonDialogSettings && (
          /** Dialog with reason selection and cancel and accept buttons */
          <ReasonDialog
            type={reasonDialogSettings.type}
            open={reasonDialogSettings.open}
            cancelCallback={reasonDialogSettings.cancelCallback}
            confirmCallback={reasonDialogSettings.confirmCallback}
            supportsExternalDeletion={reasonDialogSettings.supportsExternalDeletion}
          />
        )}

        {/** Dialog with cancel and accept buttons */}
        <Dialog
          open={!!confirmationDialogSettings?.open}
          type="confirm"
          onClose={confirmationDialogSettings?.onClose}
          dialogActions={confirmationDialogSettings?.dialogActions}
          closeOnOutsideClick
        >
          {confirmationDialogSettings?.children}
        </Dialog>
      </>
    </NavigationBlocker>
  ) : undefined;
};

// oxlint-disable-next-line typescript/no-empty-interface typescript/no-empty-object-type
interface IPatientRecords extends ICapabilityContextProps {}

export default withCapabilities(PatientRecords);
